/**
 * useTaskAssignments.ts
 * Phase 8 item 8.1 — Single source of truth for EmployeeAssignment data across
 * the Phase-2 employee planning view (REQ-07).
 *
 * Previously the "top" task grid (Phase2TaskGrid/InlineAssigneePicker) and the
 * "bottom" task-weekly schedule grid (Phase2WeeklyGrid) each maintained their
 * own independent fetch for the same EmployeeAssignment rows — using two
 * different API endpoints (/tasks/:key/assignees and
 * /employee-assignments/task/:key) whose data is identical but whose React
 * state was not shared. A successful assign in one panel did not invalidate the
 * other, so the panels could show different people for the same task.
 *
 * This hook owns the one canonical Record<taskKey, TaskAssignee[]> for a given
 * task list. Both panels receive it as a prop and call reloadOne / reloadAll
 * when a mutation completes. The effect: any mutation anywhere immediately
 * updates both panels from the same in-memory state after a single re-fetch of
 * the affected task.
 *
 * API used: GET /api/schedule/tasks/:scheduleTaskKey/assignees via
 * listTaskAssignees (scheduleTasks.ts). The /employee-assignments/task/:key
 * endpoint carries the same rows (see scheduleRoutes.ts comment for
 * BUG-2026-0099) and is now unused for the data-fetch path — it remains in
 * service for direct access patterns but Phase2WeeklyGrid no longer calls it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { listTaskAssignees, type TaskAssignee, type TaskWithBookings } from '../../../../services/scheduleTasks';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Map of ScheduleTaskKey → active EmployeeAssignment rows for that task. */
export type AssignmentMap = Record<string, TaskAssignee[]>;

export interface UseTaskAssignmentsReturn {
  /** The single canonical assignment map shared by both panels. */
  assignmentMap: AssignmentMap;
  /** True while the initial parallel fetch is in-flight (first render). */
  loadingAssignments: boolean;
  /**
   * Re-fetch the assignments for one task.
   * Call this after any assign / unassign / update in either panel.
   */
  reloadOne: (taskKey: string) => Promise<void>;
  /**
   * Re-fetch assignments for ALL tasks (e.g. on-enter-to-Phase-2 refresh).
   * Non-blocking — fires and forgets; individual task states update as they resolve.
   */
  reloadAll: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Canonical assignment data for a set of ScheduleTask rows.
 *
 * @param tasks  The task list currently shown in Phase 2. When the list changes
 *               (new scenario or source loaded) the hook re-fetches.
 * @param scenarioKey  Active draft scenario key. Forwarded to listTaskAssignees
 *               so the chips + weekly grid apply the SAME committed+draft overlay
 *               as the capacity table (one consistent, scenario-scoped set).
 *               Pass undefined for the non-scenario task table (unfiltered).
 */
export function useTaskAssignments(
  tasks: TaskWithBookings[],
  scenarioKey?: string,
): UseTaskAssignmentsReturn {
  const [assignmentMap, setAssignmentMap] = useState<AssignmentMap>({});
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  // Stable ref of current tasks so callbacks that use tasks don't need tasks
  // in their dependency arrays (avoids infinite loops when tasks ref changes).
  const tasksRef = useRef<TaskWithBookings[]>(tasks);
  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);

  // Stable ref of the active scenario key so the reload callbacks stay stable
  // (don't re-create on scenario change) while still reading the latest value.
  const scenarioKeyRef = useRef<string | undefined>(scenarioKey);
  useEffect(() => {
    scenarioKeyRef.current = scenarioKey;
  }, [scenarioKey]);

  // ── Re-fetch a single task's assignments ────────────────────────────────

  const reloadOne = useCallback(async (taskKey: string): Promise<void> => {
    try {
      const rows = await listTaskAssignees(taskKey, scenarioKeyRef.current);
      setAssignmentMap((prev) => ({ ...prev, [taskKey]: rows }));
    } catch {
      // Swallow — the chips/bars simply won't refresh if this call fails.
      // The user can click Refresh to retry.
    }
  }, []);

  // ── Re-fetch all tasks in parallel ─────────────────────────────────────

  const reloadAll = useCallback((): void => {
    const currentTasks = tasksRef.current;
    if (currentTasks.length === 0) return;
    void Promise.all(
      currentTasks.map(async (t) => {
        try {
          const rows = await listTaskAssignees(t.ScheduleTaskKey, scenarioKeyRef.current);
          return [t.ScheduleTaskKey, rows] as const;
        } catch {
          return [t.ScheduleTaskKey, [] as TaskAssignee[]] as const;
        }
      }),
    ).then((results) => {
      const next: AssignmentMap = {};
      for (const [key, rows] of results) next[key] = rows;
      setAssignmentMap(next);
    });
  }, []); // stable — tasksRef/scenarioKeyRef keep it current without re-creating

  // ── Initial load (and reload when the task list / scenario changes) ─────

  useEffect(() => {
    if (tasks.length === 0) {
      setAssignmentMap({});
      return;
    }
    setLoadingAssignments(true);
    void Promise.all(
      tasks.map(async (t) => {
        try {
          const rows = await listTaskAssignees(t.ScheduleTaskKey, scenarioKey);
          return [t.ScheduleTaskKey, rows] as const;
        } catch {
          return [t.ScheduleTaskKey, [] as TaskAssignee[]] as const;
        }
      }),
    )
      .then((results) => {
        const next: AssignmentMap = {};
        for (const [key, rows] of results) next[key] = rows;
        setAssignmentMap(next);
      })
      .finally(() => {
        setLoadingAssignments(false);
      });
  // Re-fetch when the task list identity OR the active scenario changes.
  // Using tasks as a dep is intentional: Phase2Panel reconstructs the array
  // only when listTasksForSource returns (not on every render), so this
  // won't create infinite loops. scenarioKey is included so switching drafts
  // re-applies the overlay even if the task identities happened to match.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, scenarioKey]);

  return { assignmentMap, loadingAssignments, reloadOne, reloadAll };
}
